/**
 * `useAllWorkflows()` - TanStack Query read hook for the cross-project
 * "all workflows for the current user" list.
 *
 * Implements the AC-1410 / AC-1377 P4.4 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (key conventions), 4 (Phase 4 project resources), and 5.2 (read-hook
 * signatures).
 *
 * ## Why this exists
 *
 * Today, `Dashboard.tsx`, `WorkflowsPage.tsx`, and `ProjectDashboard.tsx`
 * read the cross-project workflow list through `useProjectStore.allWorkflows`
 * + `loadAllWorkflows`. That path has no caching, no request deduplication,
 * and forces every workflow mutation (create / update / rename / delete) to
 * either:
 *
 *   1. Manually call `loadAllWorkflows()` after the mutation lands
 *      (which most of the migrated mutation hooks already do via
 *      `queryClient.invalidateQueries({ queryKey: queryKeys.workflows() })`
 *      - that invalidation is a no-op until SOMEONE subscribes to the
 *      key), or
 *   2. Mutate the store's `allWorkflows` array directly (which the store
 *      currently does for create / update / delete - but renames go
 *      through the workflow detail mutation hooks now and bypass the
 *      store).
 *
 * The second path is the source of the AC-1381 `seedFromList` workaround
 * (already removed in AC-1401, but the project store's `allWorkflows`
 * slice still feeds the editor's read-side cache). Once this hook lands
 * AND the Dashboard / WorkflowsPage / ProjectDashboard consumers migrate
 * to it, every workflow mutation's existing `invalidateQueries` call
 * becomes a real refresh trigger - no manual `loadAllWorkflows()` reload
 * needed. AC-1412 deletes the project store's `allWorkflows` slice once
 * all consumers are migrated.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getAllWorkflows()` with cache key
 *   `queryKeys.workflows()` (the AC-1396 factory; resolves to
 *   `['workflows']`). The list key is intentionally a sibling of
 *   `queryKeys.workflow(id)` (not a prefix) so invalidating the list does
 *   not refetch every individual workflow detail and vice versa - see
 *   `queryKeys.ts` JSDoc.
 *
 * - Unlike `useAgents` / `useModels` / `useRegistries`, this hook takes
 *   NO `projectId` argument. The endpoint
 *   (`GET /api/v1/orchestrations`) is scoped by the authenticated user
 *   on the backend; the frontend does not pass a filter. Pages that need
 *   per-project filtering (`Dashboard`, `WorkflowsPage`,
 *   `ProjectDashboard`) do that filtering in render via
 *   `data.filter(w => String(w.project_id) === projectId)` - same
 *   semantics as today's `allWorkflows.filter(...)` in those pages.
 *
 * - There is no `enabled` guard for an "empty ID" case because there is
 *   no ID. The hook ALWAYS fetches when subscribed; callers can pass
 *   `{ enabled: false }` if they want to defer (e.g. waiting on auth).
 *
 * - Caller-provided `options` flow through to `useQuery`. The caller's
 *   `enabled` (if provided) wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` - those are owned by
 *   the hook (the whole point of centralizing the key factory is to
 *   prevent call-site key drift). The `Options` type below excludes them.
 *
 * ## Cache parity with workflow mutation hooks
 *
 * `useUpdateWorkflow` (AC-1402) and `useRenameWorkflow` (AC-1405) already
 * invalidate `queryKeys.workflows()` on success. The store's
 * `createWorkflow` / `deleteWorkflow` paths still mutate the
 * `allWorkflows` array directly today, but AC-1411 / AC-1412 will route
 * those through hooks too. Until then, this hook is the read side; the
 * existing mutation invalidations already target its key.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { Workflow } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook - letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseAllWorkflowsOptions = Omit<
  UseQueryOptions<Workflow[], Error, Workflow[], ReturnType<typeof queryKeys.workflows>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<Workflow[]>` from TanStack Query.
 *
 * @param options Optional `useQuery` options. `queryKey` and `queryFn` are
 *                excluded from this type because they are owned by the hook.
 *
 * @example
 *   const { data: workflows, isLoading } = useAllWorkflows();
 *   if (isLoading) return <Spinner />;
 *   return <WorkflowList workflows={workflows ?? []} />;
 *
 * @example
 *   // Defer the fetch until something else has settled.
 *   const { isAuthenticated } = useAuth();
 *   useAllWorkflows({ enabled: isAuthenticated });
 */
export function useAllWorkflows(
  options?: UseAllWorkflowsOptions,
): UseQueryResult<Workflow[], Error> {
  return useQuery<Workflow[], Error, Workflow[], ReturnType<typeof queryKeys.workflows>>({
    queryKey: queryKeys.workflows(),
    queryFn: () => apiService.getAllWorkflows(),
    ...options,
  });
}

export default useAllWorkflows;
